import { useForm } from "@tellescope/forms";
import { ParamsForMethod, useTellescopeMutation } from "@tellescope/tanstack-query";
import {
  Button,
  Checkbox,
  DateTimePicker,
  Label,
  NumberInput,
  RadioGroupButtonItem,
  RadioGroupButtons,
  Switch,
} from "@tellescope/ui";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

export interface AddBPReadingSubsectionProps {
  enduserId: string;
  onSubmit: () => void;
  onCancel: () => void;
}

export const AddBPReadingSubsection = ({ enduserId, onSubmit, onCancel }: AddBPReadingSubsectionProps) => {
  const { t } = useTranslation("vitals");
  const initialTimestampRef = useRef(new Date());

  const createObservationsMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "createSome",
    onSuccess: onSubmit,
  });

  const form = useForm({
    fields: {
      timestamp: {
        type: "dateTime",
        initialValue: initialTimestampRef.current,
      },
      estimatedTime: {
        type: "checkbox",
        initialValue: false,
      },
      medStatus: {
        type: "radio",
        initialValue: "Medication: Unknown",
      },
      irregularHearbeat: {
        type: "checkbox",
        initialValue: false,
      },
      sbp: {
        type: "number",
        initialValue: undefined,
        required: true,
      },
      dbp: {
        type: "number",
        initialValue: undefined,
        required: true,
      },
      bpm: {
        type: "number",
        initialValue: undefined,
      },
    },
    validateOn: "change",
    onSubmit: (values) => {
      const requests: ParamsForMethod<"enduser_observations", "createSome"> = [
        {
          category: "vital-signs",
          enduserId,
          measurement: {
            unit: "SBP",
            value: values.sbp,
          },
          medStatus: values.medStatus,
          recordedAt: values.timestamp,
          source: "tellescope-2-provider-webapp",
          status: "final",
          timestamp: values.timestamp,
          timestampIsEstimated: values.estimatedTime,
        },
        {
          category: "vital-signs",
          enduserId,
          measurement: {
            unit: "DBP",
            value: values.dbp,
          },
          medStatus: values.medStatus,
          recordedAt: values.timestamp,
          source: "tellescope-2-provider-webapp",
          status: "final",
          timestamp: values.timestamp,
          timestampIsEstimated: values.estimatedTime,
        },
      ];

      if (values.bpm !== undefined) {
        requests.push({
          category: "vital-signs",
          enduserId,
          measurement: {
            unit: "BPM",
            value: values.bpm,
          },
          medStatus: values.medStatus,
          irregularHeartbeat: values.irregularHearbeat,
          recordedAt: values.timestamp,
          source: "tellescope-2-provider-webapp",
          status: "final",
          timestamp: values.timestamp,
          timestampIsEstimated: values.estimatedTime,
          showWithPlotsByUnit: ["SBP"],
        });
      }

      createObservationsMutation.mutate(requests);
    },
  });

  return (
    <form className="flex flex-col gap-6" {...form.getFormProps()}>
      <DateTimePicker {...form.getCustomFieldProps("timestamp")} className="w-fit" label={t("Date & Time")} />
      <span className="flex items-center gap-2">
        <Checkbox
          id="estimated-time"
          checked={form.values.estimatedTime}
          onCheckedChange={(checked) => form.setFieldValue("estimatedTime", checked !== false)}
        />
        <Label htmlFor="estimated-time" className="font-normal cursor-pointer">
          {t("Time is estimated")}
        </Label>
      </span>
      <div className="space-y-2">
        <Label htmlFor="med-status">{t("Medication Status")}</Label>
        <RadioGroupButtons
          {...form.getCustomFieldProps("medStatus")}
          unselectedVariant="outline"
          selectedVariant="default"
          className="w-full grid grid-cols-3 gap-2"
          id="med-status"
        >
          <RadioGroupButtonItem value="Medication: Before Meds">{t("Before Meds")}</RadioGroupButtonItem>
          <RadioGroupButtonItem value="Medication: After Meds">{t("After Meds")}</RadioGroupButtonItem>
          <RadioGroupButtonItem value="Medication: Unknown">{t("Unknown")}</RadioGroupButtonItem>
        </RadioGroupButtons>
      </div>
      <span className="flex justify-between items-center">
        <Label htmlFor="irregular-heartbeat">{t("Irregular Heartbeat Detected (for BPM)")}</Label>
        <Switch
          id="irregular-heartbeat"
          checked={form.values.irregularHearbeat}
          onCheckedChange={(checked) => form.setFieldValue("irregularHearbeat", checked !== false)}
        />
      </span>
      <div className="space-y-3">
        <Label htmlFor="sbp" required>
          {t("SBP (Systolic)")}
        </Label>
        <NumberInput
          id="sbp"
          placeholder={t("e.g. 120")}
          {...form.getFieldProps("sbp")}
          onChange={(e) => form.setFieldValue("sbp", e ?? 0)}
        />
      </div>
      <div className="space-y-3">
        <Label htmlFor="dbp" required>
          {t("DBP (Diastolic)")}
        </Label>
        <NumberInput
          id="dbp"
          placeholder={t("e.g. 80")}
          {...form.getCustomFieldProps("dbp")}
          onChange={(e) => form.setFieldValue("dbp", e ?? 0)}
        />
      </div>
      <div className="space-y-3">
        <Label htmlFor="bpm">{t("Heart Rate (BPM)")}</Label>
        <NumberInput
          id="bpm"
          placeholder={t("e.g. 72")}
          {...form.getCustomFieldProps("bpm")}
          onChange={(e) => form.setFieldValue("bpm", e ?? 0)}
        />
      </div>
      <div className="w-full grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!form.isValid} type="submit" loading={createObservationsMutation.isPending}>
          {t("Save readings")}
        </Button>
      </div>
    </form>
  );
};
